"use client";

import { useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import articleApi from "@/service/modules/articleApi";
import { base64Decode } from "@/utils/utils";
import { Image, ImagePreview, Skeleton } from "react-vant";
import TabBar from "../global/TabBar";

interface ArticleApiItem {
  title: string;
  content: string;
  [key: string]: unknown;
}

interface ArticleTabItem extends ArticleApiItem {
  id: number;
  name: string;
  content: string;
}

interface Props {
  articleName: string;
  banner?: string;
  backTopClassName?: string;
}

export default function ArticleTemplate({
  articleName,
  banner,
  backTopClassName,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tabList, setTabList] = useState<ArticleTabItem[]>([]);
  const [imagesArr, setImagesArr] = useState<string[]>([]);
  const [imgVisible, setImgVisible] = useState(false);
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(true);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  // 获取文章
  useEffect(() => {
    if (!articleName) return;

    const fetchArticle = async () => {
      try {
        setLoading(true);
        const data: { data?: { article?: ArticleApiItem[] } } =
          await articleApi.getArticleContent(articleName);

        const { article } = data.data;

        const newTabList =
          article?.map((item: ArticleApiItem, index: number) => ({
            ...item,
            name: item.title,
            id: index,
            content: base64Decode(item.content),
          })) || [];

        setTabList(newTabList);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error(e);
      }
    };

    fetchArticle();
  }, [articleName]);

  useEffect(() => {
    const activeTab = searchParams.get("activeTab");

    if (activeTab) {
      // 判断参数是否合规，合规参数才进行tab赋值
      const target = tabList.filter(
        (_, index: number) => index == Number(activeTab),
      );

      if (target.length) handleTabChange(Number(activeTab));
    }
  }, [searchParams, tabList]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const backTopSelector = backTopClassName || "[class*='back-top']";
      if (target.closest(backTopSelector)) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return;
      }

      if (target instanceof HTMLImageElement) {
        setImgVisible(true);
        setImagesArr([target.src]);
      }
    };

    container.addEventListener("click", handleClick);

    return () => {
      container.removeEventListener("click", handleClick);
    };
  }, [loading, backTopClassName]);

  return (
    <div className="article-page">
      {banner && (
        <Image
          className="article-banner"
          src={banner}
          alt="article"
          width="100%"
          height="auto"
        />
      )}

      {tabList.length > 1 && (
        <TabBar
          tabList={tabList}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      )}
      {loading ? (
        <Skeleton row={8} style={{ margin: "24px 0" }} />
      ) : (
        <div
          ref={containerRef}
          dangerouslySetInnerHTML={{
            __html: tabList[activeTab]?.content || "",
          }}
        ></div>
      )}

      <ImagePreview
        visible={imgVisible}
        onClose={() => setImgVisible(false)}
        images={imagesArr}
        showIndicators
        showIndex={false}
      />
    </div>
  );
}